<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import metaSdk from '@/services/metaSdk';

export default {
  name: 'CanaisWhatsapp',
  components: { Layout, PageHeader },
  data() {
    return {
      canais: [],
      uso: {},                 // canal_id -> { limite, usado, saldo, ilimitado }
      salvandoLimite: {},      // canal_id -> bool
      config: { app_id: null, config_id: null, configurado: false, graph_version: 'v20.0' },
      carregando: true,
      conectando: false,
      erro: null,
      sucesso: null,
      // Modal de registro Cloud API
      registroModal: null,    // { canal } quando aberto
      registroPin: '',
      registrando: false,
      registroErro: null,
    };
  },
  async mounted() {
    await Promise.all([this.fetchConfig(), this.fetchCanais()]);
    await this.fetchUso();
  },
  methods: {
    async fetchUso() {
      try {
        const { data } = await axios.get('/canal/whatsapp/uso');
        const mapa = {};
        (Array.isArray(data) ? data : []).forEach((u) => { mapa[u.id] = u; });
        this.uso = mapa;
      } catch (_e) { /* uso é informativo — não bloqueia a tela */ }
    },
    async salvarLimite(canal) {
      const valor = Number(canal.tier_diario);
      if (!Number.isInteger(valor) || valor < 1) {
        this.erro = 'O limite diário deve ser um número inteiro maior ou igual a 1.';
        return;
      }
      this.erro = null;
      this.salvandoLimite = { ...this.salvandoLimite, [canal.id]: true };
      try {
        await axios.put(`/canal/whatsapp/${canal.id}`, { tier_diario: valor });
        this.sucesso = `Limite diário do número ${canal.telefone_formatado || canal.phone_number_id} salvo: ${valor}/dia.`;
        await this.fetchUso();
      } catch (error) {
        const apiMsg = error.response && error.response.data && (error.response.data.error || error.response.data.details);
        this.erro = apiMsg || `Falha ao salvar limite: ${error.message}`;
      } finally {
        this.salvandoLimite = { ...this.salvandoLimite, [canal.id]: false };
      }
    },
    usoClasse(canalId) {
      const u = this.uso[canalId];
      if (!u || u.ilimitado || u.saldo == null) return 'text-muted';
      if (u.saldo <= 0) return 'text-danger fw-semibold';
      if (u.limite && u.saldo <= Math.max(1, u.limite * 0.1)) return 'text-warning fw-semibold';
      return 'text-success fw-semibold';
    },
    async fetchConfig() {
      try {
        this.config = await metaSdk.fetchConfig();
      } catch (error) {
        this.erro = `Falha ao buscar config: ${error.message}`;
      }
    },
    async fetchCanais() {
      this.carregando = true;
      try {
        const { data } = await axios.get('/canal/whatsapp', {
          params: { order: JSON.stringify([['conectado_em', 'desc']]) },
        });
        this.canais = Array.isArray(data) ? data : [];
      } catch (error) {
        this.erro = `Falha ao listar canais: ${error.message}`;
      } finally {
        this.carregando = false;
      }
    },
    async conectar() {
      this.erro = null;
      this.sucesso = null;
      this.conectando = true;
      try {
        if (!this.config.configurado) {
          this.erro = 'Configure META_APP_ID e META_CONFIG_ID no .env do backend antes de conectar.';
          return;
        }
        await metaSdk.init(this.config.app_id, this.config.graph_version);
        const { code, sessionInfo } = await metaSdk.startEmbeddedSignup(this.config.config_id);

        const payload = { code };
        // Se o popup já entregou waba_id e phone_number_id, anexa.
        if (sessionInfo && sessionInfo.data) {
          if (sessionInfo.data.waba_id) payload.waba_id = sessionInfo.data.waba_id;
          if (sessionInfo.data.phone_number_id) payload.phone_number_id = sessionInfo.data.phone_number_id;
        }

        const { data } = await axios.post('/canal/whatsapp/conectar', payload);
        const qtd = (data.conectados || []).length;
        this.sucesso = `${qtd} canal(is) conectado(s) com sucesso.`;
        await this.fetchCanais();
      } catch (error) {
        const apiMsg = error.response && error.response.data && (error.response.data.error || error.response.data.message);
        this.erro = apiMsg ? `${apiMsg}` : (error.message || 'Falha desconhecida.');
      } finally {
        this.conectando = false;
      }
    },
    abrirRegistro(canal) {
      this.registroModal = { canal };
      this.registroPin = '';
      this.registroErro = null;
    },
    fecharRegistro() {
      if (this.registrando) return;
      this.registroModal = null;
      this.registroPin = '';
      this.registroErro = null;
    },
    async confirmarRegistro() {
      if (!this.registroModal) return;
      const pin = (this.registroPin || '').trim();
      if (!/^\d{6}$/.test(pin)) {
        this.registroErro = 'O PIN deve ter exatamente 6 dígitos numéricos.';
        return;
      }
      this.registroErro = null;
      this.registrando = true;
      try {
        await axios.post(`/canal/whatsapp/registrar/${this.registroModal.canal.id}`, { pin });
        this.sucesso = `Número ${this.registroModal.canal.telefone_formatado || ''} registrado com sucesso na Cloud API.`;
        this.registroModal = null;
        this.registroPin = '';
        await this.fetchCanais();
      } catch (error) {
        const data = error.response && error.response.data;
        const metaMsg = data && data.meta && data.meta.message;
        this.registroErro = metaMsg || (data && (data.error || data.details)) || error.message;
      } finally {
        this.registrando = false;
      }
    },
    async desconectar(canal) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Desconectar canal ${canal.telefone_formatado || canal.phone_number_id}? O histórico será preservado.`)) return;
      try {
        await axios.put(`/canal/whatsapp/desconectar/${canal.id}`);
        await this.fetchCanais();
      } catch (error) {
        this.erro = `Falha ao desconectar: ${error.message}`;
      }
    },
    /**
     * Desfaz um "Desconectar".
     *
     * Só religa o registro do nosso lado — não revalida nada na Meta. Serve
     * para o caso comum (clique errado no botão vermelho), que até aqui não
     * tinha volta: canal inativo não mostra botão nenhum, e a tela de campanhas
     * só lista canais ativos, então o número sumia de lá sem explicação.
     */
    async reativar(canal) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        `Reativar ${canal.telefone_formatado || canal.display_name || canal.phone_number_id}?

`
        + 'Isto religa o canal aqui no sistema. As credenciais na Meta não são '
        + 'revalidadas agora — se o número tiver sido desconectado lá, o envio '
        + 'falha e será preciso "Re-registrar" com o PIN.',
      )) return;
      try {
        await axios.put(`/canal/whatsapp/reativar/${canal.id}`);
        await this.fetchCanais();
      } catch (error) {
        this.erro = `Falha ao reativar: ${error.response?.data?.error || error.message}`;
      }
    },
    statusBadge(status) {
      const map = {
        ativo:     'bg-success-subtle text-success',
        pendente:  'bg-warning-subtle text-warning',
        inativo:   'bg-secondary-subtle text-secondary',
        bloqueado: 'bg-danger-subtle text-danger',
      };
      return map[status] || 'bg-light';
    },
    qualityBadge(quality) {
      if (!quality) return 'bg-light text-muted';
      const map = { GREEN: 'bg-success-subtle text-success', YELLOW: 'bg-warning-subtle text-warning', RED: 'bg-danger-subtle text-danger' };
      return map[quality] || 'bg-light text-muted';
    },
    formatarData(d) {
      if (!d) return '—';
      try { return new Date(d).toLocaleString('pt-BR'); } catch (_e) { return '—'; }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Canais WhatsApp" pageTitle="Canais WhatsApp" />

    <BCard no-body>
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 class="m-0">Contas WhatsApp Business conectadas</h5>
          <small class="text-muted d-block">Conecte uma conta via Cadastro Incorporado da Meta. Requer App BSP aprovado.</small>
          <small class="text-muted d-block">
            <i class="ri-information-line me-1"></i>
            O <strong>limite diário</strong> é o teto de conversas iniciadas (templates) por dia que a Meta autoriza para o número.
            Vale para campanhas <em>e</em> disparos manuais juntos. Ajuste-o aqui conforme a Meta for aumentando o seu tier.
          </small>
        </div>
        <button
          type="button"
          class="btn btn-success"
          :disabled="conectando || !config.configurado"
          @click="conectar"
        >
          <i v-if="conectando" class="ri-loader-4-line spin me-1"></i>
          <i v-else class="ri-whatsapp-line me-1"></i>
          {{ conectando ? 'Conectando...' : 'Conectar conta WhatsApp' }}
        </button>
      </div>

      <div class="card-body">
        <div v-if="!config.configurado" class="alert alert-warning">
          <i class="ri-error-warning-line me-1"></i>
          Backend Meta ainda não configurado. Defina <code>META_APP_ID</code>, <code>META_APP_SECRET</code>,
          <code>META_SYSTEM_USER_TOKEN</code> e <code>META_CONFIG_ID</code> no <code>backend/.env</code> e reinicie.
        </div>

        <div v-if="erro" class="alert alert-danger">
          <i class="ri-error-warning-line me-1"></i>{{ erro }}
        </div>
        <div v-if="sucesso" class="alert alert-success">
          <i class="ri-checkbox-circle-line me-1"></i>{{ sucesso }}
        </div>

        <div v-if="carregando" class="text-center text-muted py-4">
          <i class="ri-loader-4-line spin"></i> Carregando canais...
        </div>
        <div v-else-if="!canais.length" class="text-center text-muted py-5">
          <i class="ri-whatsapp-line" style="font-size: 2rem;"></i>
          <div class="mt-2">Nenhum canal conectado. Clique em "Conectar conta WhatsApp" pra começar.</div>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Número</th>
                <th>Nome</th>
                <th>WABA</th>
                <th>Qualidade</th>
                <th>Limite diário (Meta)</th>
                <th>Status</th>
                <th>Conectado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in canais" :key="c.id">
                <td><strong>{{ c.telefone_formatado || c.phone_number_id }}</strong></td>
                <td>{{ c.display_name || '—' }}</td>
                <td><code class="small">{{ c.waba_id }}</code></td>
                <td><span class="badge" :class="qualityBadge(c.quality_rating)">{{ c.quality_rating || '—' }}</span></td>
                <td style="min-width: 200px;">
                  <div class="d-flex align-items-center gap-1">
                    <input
                      v-model.number="c.tier_diario"
                      type="number"
                      min="1"
                      step="1"
                      class="form-control form-control-sm"
                      style="max-width: 100px;"
                      title="Máximo de conversas iniciadas (templates) por dia autorizado pela Meta"
                      @keyup.enter="salvarLimite(c)"
                    />
                    <span class="text-muted small">/dia</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="salvandoLimite[c.id]"
                      title="Salvar limite diário"
                      @click="salvarLimite(c)"
                    >
                      <i v-if="salvandoLimite[c.id]" class="ri-loader-4-line spin"></i>
                      <i v-else class="ri-save-3-line"></i>
                    </button>
                  </div>
                  <small v-if="uso[c.id]" class="d-block mt-1">
                    <span :class="usoClasse(c.id)">{{ uso[c.id].usado }}</span>
                    <span class="text-muted"> usados hoje</span>
                    <span v-if="uso[c.id].saldo != null" class="text-muted"> · {{ uso[c.id].saldo }} restantes</span>
                  </small>
                </td>
                <td><span class="badge text-capitalize" :class="statusBadge(c.status)">{{ c.status }}</span></td>
                <td>{{ formatarData(c.conectado_em) }}</td>
                <td>
                  <div class="d-flex gap-1 flex-wrap">
                    <button
                      v-if="c.status !== 'inativo'"
                      type="button"
                      class="btn btn-sm"
                      :class="c.status === 'pendente' ? 'btn-success' : 'btn-outline-success'"
                      title="Cria/recria a conta na Cloud API e define o PIN (POST /{phone_id}/register)"
                      @click="abrirRegistro(c)"
                    >
                      <i class="ri-shield-check-line me-1"></i>
                      {{ c.status === 'pendente' ? 'Registrar' : 'Re-registrar' }}
                    </button>
                    <button
                      v-if="c.status === 'ativo'"
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="desconectar(c)"
                    >
                      <i class="ri-link-unlink-m me-1"></i>Desconectar
                    </button>
                    <button
                      v-if="c.status === 'inativo'"
                      type="button"
                      class="btn btn-sm btn-success"
                      title="Religa o canal no sistema (não revalida as credenciais na Meta)"
                      @click="reativar(c)"
                    >
                      <i class="ri-link-m me-1"></i>Reativar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </BCard>

    <!-- Modal de registro Cloud API (PIN do 2FA) -->
    <Teleport to="body">
      <div v-if="registroModal" class="modal-backdrop-custom" @click.self="fecharRegistro">
        <div class="modal-card shadow-lg">
          <header class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <h6 class="m-0">
              <i class="ri-shield-check-line me-1"></i>
              Registrar número na Cloud API
            </h6>
            <button type="button" class="btn btn-link p-0 text-body" :disabled="registrando" @click="fecharRegistro">
              <i class="ri-close-line" style="font-size: 1.4rem;"></i>
            </button>
          </header>

          <div class="px-3 py-3">
            <div class="mb-3 small">
              <div class="mb-1">
                <strong>{{ registroModal.canal.telefone_formatado || registroModal.canal.phone_number_id }}</strong>
                <span v-if="registroModal.canal.display_name" class="text-muted"> — {{ registroModal.canal.display_name }}</span>
              </div>
              <div class="text-muted">
                Informe o PIN de 6 dígitos da <strong>verificação em duas etapas</strong> deste número.
                Se ainda não tem 2FA configurado, defina um PIN qualquer agora — a Meta vai usá-lo
                como PIN do número (anote em local seguro).
              </div>
            </div>

            <label class="form-label small">PIN (6 dígitos)</label>
            <input
              v-model="registroPin"
              type="text"
              inputmode="numeric"
              maxlength="6"
              autocomplete="off"
              class="form-control form-control-lg text-center pin-input"
              placeholder="------"
              :disabled="registrando"
              @keyup.enter="confirmarRegistro"
            />

            <div v-if="registroErro" class="alert alert-danger mt-2 mb-0 py-2 small">
              <i class="ri-error-warning-line me-1"></i>{{ registroErro }}
            </div>
          </div>

          <footer class="d-flex justify-content-end gap-2 px-3 py-2 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="registrando" @click="fecharRegistro">
              Cancelar
            </button>
            <button type="button" class="btn btn-sm btn-success" :disabled="registrando || registroPin.length !== 6" @click="confirmarRegistro">
              <i v-if="registrando" class="ri-loader-4-line spin me-1"></i>
              <i v-else class="ri-shield-check-line me-1"></i>
              {{ registrando ? 'Registrando...' : 'Registrar' }}
            </button>
          </footer>
        </div>
      </div>
    </Teleport>
  </Layout>
</template>

<style scoped>
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
/* Modal — usa tokens do tema pra rodar light + dark sem patch. */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-card {
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.5rem;
  width: 100%;
  max-width: 460px;
}
.pin-input {
  letter-spacing: 0.5rem;
  font-variant-numeric: tabular-nums;
}
</style>
